import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import ServicesList from './ServicesList';


const AppointmentCard = ({ 
  appointment, 
  variant = 'default', 
  showTime = false,
  height,
  onClick,
  isOverlapping = false,
  showOnlyTimeAndVehicle = false
}) => {
  const theme = useTheme();
  const { isDark } = useAppTheme();

  if (!appointment) return null;

  // Status color mapping (matching AppointmentsList)
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'upcoming':
        return 'primary';
      case 'ongoing':
        return 'warning';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'no_show':
        return 'error';
      default:
        return 'default';
    }
  };

  // Format status display (matching AppointmentsList)
  const formatStatusDisplay = (status) => {
    switch (status?.toLowerCase()) {
      case 'no_show':
        return 'No show';
      case 'upcoming':
        return 'Upcoming';
      case 'ongoing':
        return 'Ongoing';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status || 'Unknown';
    }
  };

  // Get background color based on status
  const getStatusBgColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'upcoming':
        return isDark ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)';
      case 'ongoing':
        return isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)';
      case 'completed':
        return isDark ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)';
      case 'cancelled':
        return isDark ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)';
      case 'no_show':
        return isDark ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)';
      default:
        return isDark ? 'rgba(158, 158, 158, 0.2)' : 'rgba(158, 158, 158, 0.1)';
    }
  };

  // Get border color based on status
  const getStatusBorderColor = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'upcoming':
        return theme.palette.primary.main;
      case 'ongoing':
        return theme.palette.warning.main;
      case 'completed':
        return theme.palette.success.main;
      case 'cancelled':
        return theme.palette.error.main;
      case 'no_show':
        return '#9c27b0';
      default:
        return theme.palette.grey[400];
    }
  };

  // Format time display
  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours) === 0 ? 12 : parseInt(hours);
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Compact variant for month view
  if (variant === 'compact') {
    return (
      <Box
        onClick={onClick}
        sx={{
          p: 0.5,
          bgcolor: getStatusBgColor(appointment.status),
          borderLeft: '3px solid',
          borderLeftColor: getStatusBorderColor(appointment.status),
          borderRadius: 1,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            bgcolor: getStatusBgColor(appointment.status),
            opacity: 0.8
          } : {},
          transition: 'opacity 0.2s ease'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          {showTime && (
            <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
              {formatTime(appointment.startTime)}
            </Typography>
          )}
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.65rem', 
              fontWeight: 'bold',
              color: 'text.primary',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {appointment.vehicleReg}
          </Typography>
        </Box>
        {!showOnlyTimeAndVehicle && (
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '0.6rem',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block'
            }}
          >
            {appointment.customerName}
          </Typography>
        )}
      </Box>
    );
  }

  // Timeline variant for week view
  if (variant === 'timeline') {
    return (
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          bgcolor: getStatusBgColor(appointment.status),
          borderLeft: '4px solid',
          borderLeftColor: getStatusBorderColor(appointment.status),
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            boxShadow: 2,
            transform: isOverlapping ? 'scale(1.01)' : 'scale(1.02)'
          } : {},
          transition: 'all 0.2s ease',
          overflow: 'hidden',
          // Adjust styling for overlapping events
          opacity: isOverlapping ? 0.95 : 1,
          mr: isOverlapping ? 0.25 : 0
        }}
      >
        <CardContent sx={{ 
          p: isOverlapping ? 0.5 : 1, 
          '&:last-child': { pb: isOverlapping ? 0.5 : 1 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Vehicle Registration */}
          <Box sx={{ mb: 0.5 }}>
            <Typography 
              variant="caption" 
              fontWeight="bold" 
              color="text.primary"
              sx={{ 
                fontSize: isOverlapping ? '0.65rem' : '0.7rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
            >
              {appointment.vehicleReg}
            </Typography>
          </Box>

          {/* Services */}
          {appointment.services && appointment.services.length > 0 && (
            <Box sx={{ mb: 0.5 }}>
              <ServicesList 
                services={appointment.services} 
                variant="timeline" 
                isOverlapping={isOverlapping}
              />
            </Box>
          )}

          {/* Customer */}
          <Box sx={{ mt: 'auto' }}>
            <Typography 
              variant="caption" 
              color="text.primary"
              sx={{ 
                fontSize: isOverlapping ? '0.6rem' : '0.65rem',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block'
              }}
            >
              {appointment.customerName}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Detailed variant for day view timeline
  if (variant === 'detailed') {
    return (
      <Card
        onClick={onClick}
        sx={{
          height: '100%',
          bgcolor: getStatusBgColor(appointment.status),
          borderLeft: '4px solid',
          borderLeftColor: getStatusBorderColor(appointment.status),
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            boxShadow: 3,
            transform: isOverlapping ? 'scale(1.005)' : 'scale(1.01)'
          } : {},
          transition: 'all 0.2s ease',
          // Adjust styling for overlapping events
          opacity: isOverlapping ? 0.95 : 1,
          mr: isOverlapping ? 0.5 : 0
        }}
      >
        <CardContent sx={{ 
          p: isOverlapping ? 1 : 2, 
          '&:last-child': { pb: isOverlapping ? 1 : 2 },
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Vehicle Registration */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isOverlapping ? 0.5 : 1, mb: 1 }}>
            <CarIcon sx={{ fontSize: isOverlapping ? 14 : 16, color: 'primary.main' }} />
            <Typography 
              variant="body2" 
              fontWeight="bold" 
              color="text.primary"
              sx={{ 
                fontSize: isOverlapping ? '0.7rem' : '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {appointment.vehicleReg}
            </Typography>
          </Box>

          {/* Services */}
          {appointment.services && appointment.services.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <ServicesList 
                services={appointment.services} 
                variant="detailed" 
                isOverlapping={isOverlapping}
              />
            </Box>
          )}

          {/* Customer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isOverlapping ? 0.5 : 1, mb: 1, mt: 'auto' }}>
            <PersonIcon sx={{ fontSize: isOverlapping ? 14 : 16, color: 'text.secondary' }} />
            <Typography 
              variant="body2" 
              color="text.primary"
              sx={{ 
                fontSize: isOverlapping ? '0.7rem' : '0.875rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {appointment.customerName}
            </Typography>
          </Box>

          {/* Status */}
          <Box>
            <Chip
              label={formatStatusDisplay(appointment.status)}
              size="small"
              color={getStatusColor(appointment.status)}
              sx={{
                fontSize: isOverlapping ? '0.6rem' : '0.7rem',
                height: isOverlapping ? 20 : 24,
                fontWeight: 'bold'
              }}
            />
          </Box>

          {/* Show time at bottom for overlapping events */}
          {showTime && isOverlapping && (
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                fontSize: '0.6rem',
                textAlign: 'center',
                mt: 0.5,
                borderTop: '1px solid',
                borderTopColor: 'divider',
                pt: 0.5
              }}
            >
              {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  }

  // List variant for sidebar
  if (variant === 'list') {
    return (
      <Card
        onClick={onClick}
        sx={{
          bgcolor: getStatusBgColor(appointment.status),
          borderLeft: '4px solid',
          borderLeftColor: getStatusBorderColor(appointment.status),
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? {
            boxShadow: 2
          } : {},
          transition: 'box-shadow 0.2s ease'
        }}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Time */}
          {showTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TimeIcon sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="body2" fontWeight="bold" color="primary.main">
                {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
              </Typography>
            </Box>
          )}

          {/* Vehicle and customer */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight="bold">
              {appointment.vehicleReg}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">
              {appointment.customerName}
            </Typography>
          </Box>

          {/* Services */}
          {appointment.services && appointment.services.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Services:
              </Typography>
              <ServicesList 
                services={appointment.services} 
                variant="list"
              />
            </Box>
          )}

          {/* Status and amount */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Chip
              label={formatStatusDisplay(appointment.status)}
              size="small"
              color={getStatusColor(appointment.status)}
              sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}
            />
            {appointment.totalAmount && (
              <Typography variant="body2" fontWeight="bold" color="text.primary">
                £{appointment.totalAmount}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Default variant
  return (
    <Card
      onClick={onClick}
      sx={{
        bgcolor: getStatusBgColor(appointment.status),
        borderLeft: '4px solid',
        borderLeftColor: getStatusBorderColor(appointment.status),
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          boxShadow: 2
        } : {},
        transition: 'box-shadow 0.2s ease'
      }}
    >
      <CardContent>
        <Typography variant="h6" component="h3">
          {appointment.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {appointment.customerName}
        </Typography>
        {showTime && (
          <Typography variant="caption" color="primary.main">
            {formatTime(appointment.startTime)} - {formatTime(appointment.endTime)}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default AppointmentCard;